import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ConfigProvider, App as AntdApp, Spin } from 'antd'
import zhCN from 'antd/locale/zh_CN'
import { lazy, Suspense, useEffect } from 'react'
import { Navigate, Route, BrowserRouter, Routes } from 'react-router-dom'

import AdminLayout from './layouts/AdminLayout'
import Login from './pages/Login'
import QuestionDetail from './pages/questions/QuestionDetail'
import QuestionList from './pages/questions/QuestionList'
import QuestionManagementPage from './pages/questions/QuestionManagementPage'
import { useAuth } from './store/auth'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // 后台数据是运营在看的，过期重取比省一次请求重要
      staleTime: 10_000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
})

const DataDashboard = lazy(() => import('./pages/dashboard/DataDashboard'))
const GrowthDashboard = lazy(() => import('./pages/dashboard/GrowthDashboard'))
const RuleConfigPage = lazy(() => import('./pages/config/RuleConfigPage'))
const ContentOperationsPage = lazy(() => import('./pages/content/ContentOperationsPage'))
const TicketWorkbench = lazy(() => import('./pages/tickets/TicketWorkbench'))
const UsersPage = lazy(() => import('./pages/users/UsersPage'))
const UserDetailPage = lazy(() => import('./pages/users/UserDetailPage'))
const AttemptsPage = lazy(() => import('./pages/records/AttemptsPage'))
const AttemptDetailPage = lazy(() => import('./pages/records/AttemptDetailPage'))
const SharesPage = lazy(() => import('./pages/records/SharesPage'))

export default function App() {
  const restore = useAuth((s) => s.restore)
  useEffect(() => {
    void restore()
  }, [restore])

  return (
    <ConfigProvider locale={zhCN}>
      <AntdApp>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <Suspense fallback={<Spin fullscreen />}>
              <Routes>
                <Route path="/login" element={<Login />} />
                <Route element={<AdminLayout />}>
                  <Route path="/dashboard" element={<DataDashboard />} />
                  <Route path="/growth" element={<GrowthDashboard />} />
                  <Route path="/config" element={<RuleConfigPage />} />
                  <Route path="/content" element={<ContentOperationsPage />} />
                  <Route path="/tickets" element={<TicketWorkbench />} />
                  <Route path="/users" element={<UsersPage />} />
                  <Route path="/users/:id" element={<UserDetailPage />} />
                  <Route path="/attempts" element={<AttemptsPage />} />
                  <Route path="/attempts/:id" element={<AttemptDetailPage />} />
                  <Route path="/shares" element={<SharesPage />} />
                  <Route path="/questions" element={<QuestionManagementPage />} />
                  <Route path="/questions/:id" element={<QuestionDetail />} />
                  {/* 复核专家的工作台就是「待复核」这一个筛选视图，不另做一套页面 */}
                  <Route
                    path="/review"
                    element={<QuestionList key="review" fixedStatus="PENDING_REVIEW" />}
                  />
                  <Route path="*" element={<DefaultRoute />} />
                </Route>
              </Routes>
            </Suspense>
          </BrowserRouter>
        </QueryClientProvider>
      </AntdApp>
    </ConfigProvider>
  )
}

function DefaultRoute() {
  const role = useAuth((state) => state.me?.role)
  return <Navigate to={role === 'REVIEWER' ? '/review' : '/dashboard'} replace />
}
